// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ValidationFields<FormData extends { [key: string]: any }> = {
  [key in keyof FormData]?: {
    exist?: string;
    email?: string;
    url?: string;
    custom?: {
      error: string;
      condition: (value: string | boolean | number) => boolean;
    };
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const validateForm = <FormData extends { [key: string]: any }>(
  form: FormData,
  validationFields?: ValidationFields<FormData>,
): { isFormValid: boolean; errors: { [key in keyof FormData]?: string } } => {
  if (!validationFields) {
    return {
      isFormValid: true,
      errors: {},
    };
  }
  const formFieldNames = [...new Set([...Object.keys(form), ...Object.keys(validationFields)])] as (keyof FormData)[];

  const errors = formFieldNames.reduce(
    (memo, formFieldName) => {
      const value = form[formFieldName];

      const validationType = validationFields[formFieldName];

      if (validationType === undefined) {
        return memo;
      }

      const nonEmptyValidation = validationType['exist'];
      const emailValidation = validationType['email'];
      const urlValidation = validationType['url'];
      const customValidation = validationType['custom'];

      if (nonEmptyValidation !== undefined) {
        const isEmptyString = typeof value === 'string' && value.trim() === '';
        if (isEmptyString || value === null || value === undefined) {
          memo[formFieldName] = nonEmptyValidation;
          return memo;
        }
      }

      if (customValidation !== undefined && !customValidation.condition(value)) {
        memo[formFieldName] = customValidation.error;
        return memo;
      }

      if (emailValidation !== undefined && !isValidEmail(value)) {
        memo[formFieldName] = emailValidation;
        return memo;
      }

      if (urlValidation !== undefined && !isValidUrl(value)) {
        memo[formFieldName] = urlValidation;
        return memo;
      }

      return memo;
    },
    {} as { [key in keyof FormData]: string },
  );

  return {
    isFormValid: Object.keys(errors).length === 0,
    errors,
  };
};

export function omitEmptyFormValues<T extends object>(data: T): T {
  return Object.fromEntries(
    Object.entries(data).filter(([, value]) => {
      if (typeof value === 'string') {
        return value.trim() !== '';
      }
      return value !== null && value !== undefined;
    }),
  ) as T;
}

export const isValidEmail = (email: string) => {
  const regex = new RegExp(
    // eslint-disable-next-line no-useless-escape
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))/.source +
      /@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.source,
  );
  return regex.test(email);
};

export const isValidUrl = (url: string) => {
  const regex = new RegExp(
    /(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&/=]*)/g,
  );
  return regex.test(url);
};
